<template>
  <div :class="t.wrapperLoose">
    <label :class="t.label">
      {{ question.prompt }}
      <span v-if="!question.required" class="text-gray-400 font-normal">(optional)</span>
    </label>
    <p
      v-if="question.subtext"
      :class="t.subtext"
      v-html="question.subtext"
    ></p>

    <!-- Photo Upload Area -->
    <div class="space-y-4">
      <!-- Current Photo Preview -->
      <div v-if="hasPhoto" class="relative inline-block">
        <img
          :src="photoUrl"
          class="max-w-xs max-h-48 rounded-lg shadow-md"
          alt="Uploaded photo"
        />
        <button
          @click="removePhoto"
          type="button"
          class="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center shadow-md hover:bg-red-600 transition"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Upload Button -->
      <div v-if="!hasPhoto">
        <label
          :class="[t.uploadArea, uploadAreaStateClass]"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="handleDrop"
        >
          <div class="flex flex-col items-center justify-center pt-5 pb-6">
            <svg
              class="w-12 h-12 mb-3 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <p :class="t.uploadAreaText">
              <span v-if="uploading">Uploading...</span>
              <span v-else>Tap to upload photo</span>
            </p>
            <p :class="[t.helperText, 'mt-1']">PNG, JPG up to 10MB</p>
          </div>
          <input
            ref="fileInput"
            type="file"
            class="hidden"
            accept="image/*"
            @change="handleFileSelect"
            :disabled="uploading"
          />
        </label>
      </div>

      <!-- Upload Progress -->
      <div v-if="uploading" class="w-full bg-gray-200 rounded-full h-2">
        <div
          class="h-2 rounded-full transition-all duration-300"
          :style="{ width: `${uploadProgress}%`, backgroundColor: 'var(--team-primary)' }"
        ></div>
      </div>
    </div>

    <!-- Error Message -->
    <p v-if="error" :class="t.error">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script>
import axios from 'axios';
import { tokensFor } from './size_tokens';
import { isHeicFile, convertHeicToJpeg } from '../../../wrestling/utilities/heic';

export default {
  name: 'QuestionPhoto',
  props: {
    question: {
      type: Object,
      required: true,
    },
    existingAnswer: {
      type: Object,
      default: null,
    },
    endpoint: {
      type: String,
      default: '',
    },
    accessKey: {
      type: String,
      default: '',
    },
    cloudfrontBaseUrl: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: 'kiosk',
      validator: (v) => ['kiosk', 'form'].includes(v),
    },
  },
  data() {
    return {
      answer: {
        attachment_id: null,
        s3_file_name: '',
        file_name: '',
        status: '',
      },
      originalAnswer: null,
      error: false,
      errorMessage: 'Please upload a photo.',
      uploading: false,
      uploadProgress: 0,
      isDragging: false,
    };
  },
  computed: {
    t() {
      return tokensFor(this.size);
    },
    uploadAreaStateClass() {
      return this.isDragging
        ? 'border-blue-500 bg-blue-50'
        : 'border-gray-300 hover:border-gray-400 bg-gray-50 hover:bg-gray-100';
    },
    hasPhoto() {
      return this.answer.s3_file_name && this.answer.s3_file_name.length > 0;
    },
    photoUrl() {
      if (!this.hasPhoto) return '';
      if (this.cloudfrontBaseUrl) {
        return `${this.cloudfrontBaseUrl}/${this.answer.s3_file_name}`;
      }
      return `${this.endpoint}/${this.answer.s3_file_name}`;
    },
    dirty() {
      if (!this.originalAnswer) {
        return this.hasPhoto;
      }
      return this.answer.s3_file_name !== this.originalAnswer.s3_file_name;
    },
    valid() {
      if (this.question.required) {
        return this.hasPhoto;
      }
      return true;
    },
  },
  watch: {
    answer: {
      deep: true,
      handler() {
        this.emit();
        if (this.valid) {
          this.error = false;
        }
      },
    },
  },
  created() {
    if (this.existingAnswer && this.existingAnswer.answer) {
      const existing = this.existingAnswer.answer;
      if (typeof existing === 'object' && existing.s3_file_name) {
        this.answer = { ...existing };
        this.originalAnswer = { ...existing };
      }
    }
    this.$notificationManager?.on('show-reg-errors', this.validate);
  },
  destroyed() {
    this.$notificationManager?.off('show-reg-errors', this.validate);
  },
  methods: {
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadFile(file);
      }
    },
    handleDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) {
        this.uploadFile(file);
      }
    },
    async uploadFile(rawFile) {
      // Browsers other than Safari can't render HEIC, so convert to JPEG before
      // uploading. On conversion failure fall back to the original file.
      let file = rawFile;
      if (isHeicFile(rawFile)) {
        try {
          file = await convertHeicToJpeg(rawFile);
        } catch (e) {
          file = rawFile;
        }
      }

      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        this.error = true;
        this.errorMessage = 'File is too large. Maximum size is 10MB.';
        return;
      }

      this.uploading = true;
      this.uploadProgress = 0;
      this.error = false;

      try {
        // Get presigned URL from server
        const presignResponse = await axios.post('/api/v1/attachments/presign', {
          filename: file.name,
          content_type: file.type,
        });

        const { presigned_url, s3_file_name, attachment_id } = presignResponse.data;

        // Upload to S3
        await axios.put(presigned_url, file, {
          headers: {
            'Content-Type': file.type,
          },
          onUploadProgress: (progressEvent) => {
            this.uploadProgress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
          },
        });

        // Update answer
        this.answer = {
          attachment_id,
          s3_file_name,
          file_name: file.name,
          status: 'uploaded',
        };

        this.uploadProgress = 100;
      } catch (err) {
        console.error('Upload failed:', err);
        this.error = true;
        this.errorMessage = 'Failed to upload photo. Please try again.';
      } finally {
        this.uploading = false;
      }
    },
    removePhoto() {
      this.answer = {
        attachment_id: null,
        s3_file_name: '',
        file_name: '',
        status: '',
      };
      // Reset file input
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    emit() {
      this.$emit('answer-changed', this.question, this.answer, this.dirty, this.valid);
    },
    validate() {
      this.error = !this.valid;
      if (this.error) {
        this.errorMessage = 'Please upload a photo.';
      }
    },
  },
};
</script>
